import { modK } from '../../lib/platform'
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import clsx from 'clsx'

import { useUploadChatAttachment, type ChatAttachment, type ChatScope } from '../../lib/data'
import { CHAT_INPUT_MAX_PX, fitToText } from '../../lib/fit-textarea'
import { ACCEPTED_IMAGE_TYPES, downscaleImage, isAcceptedImage } from '../../lib/image'
import { ArrowUp, Mic, Paperclip } from './icons'
import { scopeDotClass } from './parts'

// ============================================================================
// THE COMPOSER — LifeOS Chat.dc.html, 11a and 11b.
//
// README §10: "White, radius 14, shadow `0 4px 16px rgba(16,24,40,.07)`. Contains
// the current scope chip with a hue dot, the input, a `quick` / `deep` segmented
// control on `fill-2`, a mic icon, and a dark send button. Below it, two quiet
// lines explain the mode and mention `{modK} to capture instead`."
//
// SENDING IS REAL SINCE V4. The composer submits to the one chat engine
// (apps/worker/src/routes/chat.ts): retrieval runs first, the answer is built
// only from what came back, and every claim carries the cards it read.
//
// WHAT IT SUBMITS TO WHEN IT CANNOT ANSWER is still a plainly-worded notice
// rather than a greyed-out bar — the AskBar's shape, kept. In demo mode there is
// no model and no key, so `sendChatMessage` refuses with a sentence saying why;
// the field keeps what was typed, so nothing is lost to a refusal.
//
// THE INPUT IS UNCONTROLLED. React never owns the value, so the browser's native
// undo stack survives — the journal composer's rule, and the one thing no gate in
// this repo can see (docs/design/FIDELITY.md). `hasText` is tracked separately
// because the send button's enabled state is a real state the mockup draws both
// ways: `ink` with text (11a), `fill` without (11b).
//
// `quick` / `deep` IS THE COST POLICY, not decoration, and that is new in V4: it
// picks the model tier the question is answered on. `quick` is Haiku, which is
// what per-item work runs on; `deep` is the Sonnet tier and is the owner opting
// in, per question (CLAUDE.md → AI cost policy).
//
// CHAT v3 ADDED PICTURES. Paste one, drop one, or press the paperclip: it is
// shrunk to 1600px in the browser (lib/image.ts — the reasoning is there) and
// uploaded straight away, so a file that is too big says so while the question
// is still half typed rather than at the moment it is sent.
//
// THE EXPLAINER COPY IS UNCHANGED, deliberately. The handoff's two quiet lines
// say what `quick` and `deep` read and how to capture instead; a third line
// about attachments would be the app explaining a paperclip.
//
// A QUESTION CAN ARRIVE FROM ANOTHER SCREEN (`draft`). The lecture page's ask bar
// and the empty thread's starter cards both hand a question to this composer
// instead of each growing a chat client of their own — there is one send path in
// this app and every mouth feeds it. The field is still UNCONTROLLED: a draft is
// written into the node imperatively, once, exactly as a suggestion chip does on
// the Interests ask bar, so the browser's undo stack survives it.
// ============================================================================

export type ChatMode = 'quick' | 'deep'

/**
 * A question handed to the composer by another screen.
 *
 * `send` is the difference between the two callers and it is a judgement about WHOSE WORDS these
 * are: the lecture ask bar carries a sentence the owner typed and submitted himself, so it sends;
 * a starter card carries a sentence the app wrote for him, so it lands in the field, focused, and
 * waits for the keystroke that makes it his.
 *
 * `key` is what makes the SAME question twice still arrive twice — the effect keys on it rather
 * than on the text.
 */
export interface ComposerDraft {
  text: string
  send: boolean
  key: number
}

/**
 * How many pictures one question may carry. FOUR, and it matches the Worker's own ceiling
 * (`MAX_ATTACHMENTS_PER_MESSAGE`) — a browser that let a fifth through would produce a refusal
 * the owner could have been told about before he chose the file.
 */
const MAX_PICTURES = 4

export function Composer({
  scope,
  placeholder,
  showExplainer,
  onSend,
  isPending = false,
  error,
  note,
  draft,
}: {
  scope: ChatScope | undefined
  placeholder: string
  /** 11a draws the two quiet lines under the card; 11b does not. */
  showExplainer: boolean
  /** A question from another screen — see `ComposerDraft`. Null until one arrives. */
  draft?: ComposerDraft | null
  /**
   * Ask. Returning a promise is what lets this component clear the field ONLY once the question
   * actually went somewhere — see the submit handler.
   */
  onSend?: (text: string, mode: ChatMode, attachmentIds: string[]) => Promise<unknown>
  isPending?: boolean
  /** What went wrong, or nothing. Rendered in words, never swallowed. */
  error?: unknown
  /** What was DEGRADED but still answered — a tier that could not be served, an index that was not bound. */
  note?: string | null
}) {
  const inputId = useId()
  const input = useRef<HTMLTextAreaElement>(null)
  const filePicker = useRef<HTMLInputElement>(null)
  const [hasText, setHasText] = useState(false)
  const [mode, setMode] = useState<ChatMode>('quick')
  const [sent, setSent] = useState(false)
  // The chips. `previewUrl` is made from the LOCAL file rather than fetched back from the API:
  // the browser already has the bytes, and a round trip to draw a thumbnail of the thing that
  // was just uploaded is a round trip for nothing.
  const [pictures, setPictures] = useState<{ attachment: ChatAttachment; previewUrl: string }[]>([])
  const [pictureError, setPictureError] = useState<string | null>(null)
  const upload = useUploadChatAttachment()

  // Object URLs are a browser-level allocation and they outlive React unless something says so.
  useEffect(
    () => () => {
      for (const p of pictures) URL.revokeObjectURL(p.previewUrl)
    },
    // Intentionally on unmount only: revoking on every change would kill the URL of a chip that
    // is still on screen. Individual removals revoke their own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /** Shrink, upload, add a chip. One file at a time, so one failure names the file that failed. */
  async function attach(files: File[]) {
    setPictureError(null)
    for (const file of files) {
      if (pictures.length >= MAX_PICTURES) {
        setPictureError(
          `Four pictures is the most one question can carry. Send these first, then the rest.`,
        )
        return
      }
      if (!isAcceptedImage(file)) {
        setPictureError(
          `${file.name || 'That file'} is not a picture this can read — PNG, JPEG or WebP.`,
        )
        continue
      }
      try {
        const shrunk = await downscaleImage(file)
        const attachment = await upload.mutateAsync(shrunk)
        setPictures((prev) => [...prev, { attachment, previewUrl: URL.createObjectURL(shrunk) }])
      } catch (error) {
        setPictureError(
          error instanceof Error ? error.message : 'That picture could not be sent, and gave no reason.',
        )
      }
    }
  }

  /**
   * ASK. Called by the form's own submit and by a `draft` that arrived with `send`, and it reads
   * the question OUT OF THE NODE rather than off `hasText`: a draft written into the field one
   * line above has not been through a render yet, so the state flag is still false while the
   * field already holds the sentence. The node is the truth here — it always was, this is the
   * uncontrolled input's whole bargain.
   */
  function submit() {
    if (isPending) return
    const text = input.current?.value.trim() ?? ''
    if (!text) return
    if (!onSend) {
      setSent(true)
      return
    }
    setSent(false)
    // THE QUESTION LEAVES THE FIELD THE MOMENT IT IS SENT — it is drawn in the conversation while
    // it is answered (owner, 2026-09-23). A REFUSAL PUTS IT BACK: demo mode, a missing key, a model
    // that would not answer — the field gets back exactly what was typed. Losing what someone wrote
    // to a failure they did not cause is the rudest possible way to say "no".
    if (input.current) {
      input.current.value = ''
      fitToText(input.current, CHAT_INPUT_MAX_PX)
    }
    setHasText(false)
    void onSend(text, mode, pictures.map((p) => p.attachment.id)).then(
      () => {
        // The chips go with the question they rode on. A picture left in the composer after
        // it has been sent would be sent twice on the next message.
        for (const p of pictures) URL.revokeObjectURL(p.previewUrl)
        setPictures([])
        setPictureError(null)
      },
      () => {
        // The reason is rendered by <Refused>; the question goes back where it was typed, unless
        // something new has been typed there meanwhile.
        if (input.current && input.current.value.trim() === '') {
          input.current.value = text
          fitToText(input.current, CHAT_INPUT_MAX_PX)
          setHasText(true)
        }
      },
    )
  }

  // A DRAFT FROM ANOTHER SCREEN, applied once per arrival. Focus goes to the field either way:
  // a question that landed in a composer nobody is typing in would be a question the owner has
  // to go and find.
  useEffect(() => {
    if (!draft || !input.current) return
    input.current.value = draft.text
    fitToText(input.current, CHAT_INPUT_MAX_PX)
    setHasText(draft.text.trim().length > 0)
    input.current.focus()
    if (draft.send) submit()
    // Keyed on the arrival, not on the text or on `submit`: re-running this because a picture
    // was attached or a mode was switched would re-type the draft over whatever is in the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.key])

  function removePicture(id: string) {
    setPictures((prev) => {
      const gone = prev.find((p) => p.attachment.id === id)
      if (gone) URL.revokeObjectURL(gone.previewUrl)
      return prev.filter((p) => p.attachment.id !== id)
    })
  }

  return (
    <div className="flex flex-none flex-col gap-[9px] px-[30px] pb-5 pt-[14px]">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        onPaste={(e) => {
          // A PASTED SCREENSHOT IS THE WHOLE POINT. `clipboardData.files` is where a screenshot
          // arrives; pasted TEXT goes to the input untouched, which is why nothing here calls
          // preventDefault unless a file was actually found.
          const files = [...e.clipboardData.files].filter((f) => f.type.startsWith('image/'))
          if (files.length === 0) return
          e.preventDefault()
          void attach(files)
        }}
        onDragOver={(e) => {
          if ([...e.dataTransfer.types].includes('Files')) e.preventDefault()
        }}
        onDrop={(e) => {
          const files = [...e.dataTransfer.files].filter((f) => f.type.startsWith('image/'))
          if (files.length === 0) return
          e.preventDefault()
          void attach(files)
        }}
        className="flex items-end gap-[13px] rounded-card border border-line-2 bg-surface px-4 py-[13px] shadow-composer"
      >
        <span className="flex flex-none items-center gap-[6px] rounded-pill bg-fill-2 px-[10px] py-[5px]">
          <span
            className={clsx('size-[5px] rounded-full', scopeDotClass(scope?.domain ?? 'everything'))}
            aria-hidden
          />
          <span className="text-[10.5px] font-semibold leading-none text-ink-2">
            {scope?.label ?? 'Everything'}
          </span>
        </span>

        <label htmlFor={inputId} className="sr-only">
          {placeholder}
        </label>
        {/* GROWS WITH THE QUESTION (owner, 2026-09-20): one row for a short ask, up to six for a
            long one, then it scrolls inside. Enter asks; Shift+Enter is a new line — the one
            keyboard habit every chat shares, and the reason this is a textarea and not an input. */}
        <textarea
          id={inputId}
          ref={input}
          rows={1}
          autoComplete="off"
          placeholder={placeholder}
          onChange={(e) => {
            fitToText(e.currentTarget, CHAT_INPUT_MAX_PX)
            setHasText(e.currentTarget.value.trim().length > 0)
            if (sent) setSent(false)
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
            e.preventDefault()
            submit()
          }}
          className="my-[2px] min-w-0 flex-1 resize-none bg-transparent text-[14px] font-normal leading-[1.45] text-ink outline-none placeholder:text-microcopy"
        />

        <div
          role="group"
          aria-label="How much to read"
          className="flex flex-none items-center gap-[3px] rounded-button bg-fill-2 p-[3px]"
        >
          {(['quick', 'deep'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
              style={{ '--mo-rule': 'var(--color-ink)' } as CSSProperties}
              className={clsx(
                'mo-ink mo-press rounded-chip px-[11px] py-[5px] text-[10.5px] leading-none',
                mode === m
                  ? 'bg-surface font-semibold text-ink shadow-thumb'
                  : 'font-medium text-muted hover:text-ink-2',
              )}
            >
              {m}
            </button>
          ))}
        </div>

        <input
          ref={filePicker}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          multiple
          className="hidden"
          onChange={(e) => {
            const files = [...(e.currentTarget.files ?? [])]
            e.currentTarget.value = ''
            void attach(files)
          }}
        />
        <button
          type="button"
          onClick={() => filePicker.current?.click()}
          disabled={upload.isPending || pictures.length >= MAX_PICTURES}
          title="Send a picture — or just paste or drop one here"
          className={clsx(
            'mo-press flex-none transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
            upload.isPending || pictures.length >= MAX_PICTURES
              ? 'cursor-not-allowed text-microcopy'
              : 'text-muted hover:text-ink-2',
          )}
        >
          <Paperclip />
          <span className="sr-only">Attach a picture</span>
        </button>

        <button
          type="button"
          aria-disabled="true"
          title="Asking out loud arrives with V1, the transcription system — the same desktop worker that turns a lecture recording into a transcript. Typing works now."
          onClick={(e) => e.preventDefault()}
          className="flex-none cursor-not-allowed text-microcopy"
        >
          <Mic />
          <span className="sr-only">Ask by voice</span>
        </button>

        <button
          type="submit"
          disabled={!hasText || isPending}
          className={clsx(
            'flex size-[30px] flex-none items-center justify-center rounded-button',
            'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
            hasText && !isPending ? 'bg-ink text-chat-lift hover:bg-ink-2' : 'bg-fill text-microcopy',
          )}
        >
          <ArrowUp />
          <span className="sr-only">Send</span>
        </button>
      </form>

      {(pictures.length > 0 || upload.isPending) && (
        <div className="flex flex-wrap items-center gap-[7px] px-[3px]">
          {pictures.map(({ attachment, previewUrl }) => (
            <span
              key={attachment.id}
              className="flex items-center gap-[6px] rounded-chip border border-line bg-surface py-[3px] pl-[3px] pr-[7px]"
            >
              <img
                src={previewUrl}
                alt="Attached picture"
                className="size-[26px] rounded-[5px] object-cover"
              />
              <button
                type="button"
                onClick={() => removePicture(attachment.id)}
                className="mo-press text-[10px] font-semibold leading-none text-microcopy transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:text-ink-2"
              >
                Remove
                <span className="sr-only"> this picture</span>
              </button>
            </span>
          ))}
          {upload.isPending && (
            <span role="status" className="text-[10.5px] leading-none text-microcopy">
              Sending the picture…
            </span>
          )}
        </div>
      )}

      {pictureError && (
        <p role="status" className="px-[3px] text-[10.5px] leading-[1.45] text-danger text-pretty">
          {pictureError}
        </p>
      )}

      {sent && <SendNotice />}
      {error != null && <Refused error={error} />}
      {/* The wait is stated in the conversation now, under the question (PendingTurn). */}
      {!isPending && error == null && note && (
        <p role="status" className="px-[3px] text-[10.5px] font-normal leading-[1.45] text-microcopy text-pretty">
          {note}
        </p>
      )}

      {showExplainer && !sent && !isPending && error == null && (
        <div className="flex items-center gap-[9px] px-[3px]">
          <span className="text-[10.5px] font-normal leading-none text-microcopy">
            quick answers read titles and summaries · deep reads full transcripts and takes longer
          </span>
          <span className="ml-auto text-[10.5px] font-normal leading-none text-microcopy">
            {modK} to capture instead
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * WHY THE QUESTION DID NOT GET AN ANSWER, in the words of whatever refused it.
 *
 * The demo dataset refusing because it has no model, and a deployment refusing because it has no
 * ANTHROPIC_API_KEY, are two different sentences and this prints whichever one it got rather than
 * one generic apology standing in for both. The handoff's copy pattern holds: name the consequence,
 * name what still works, offer exactly one action — and there is no action here, because pressing
 * something would not set a secret.
 */
function Refused({ error }: { error: unknown }) {
  const message =
    error instanceof Error
      ? error.message
      : 'The question could not be sent, and whatever refused it gave no reason.'
  return (
    <div
      role="status"
      className="flex items-start gap-[10px] rounded-row border border-dashed border-dash bg-surface-2 px-[13px] py-[11px]"
    >
      <span className="mt-[1px] flex-none rounded-pill bg-fill-2 px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.6] tracking-[0.04em] text-muted">
        NOT ANSWERED
      </span>
      <p className="text-[11.5px] leading-[1.45] text-ink-3 text-pretty">
        {message} Your question is still in the field.
      </p>
    </div>
  )
}

/**
 * The V0–V3 state, kept for a Composer rendered with no `onSend` at all. The handoff's copy
 * pattern, in order: name the consequence, name what still works, offer exactly one action — and
 * here there is no action, for the same reason the rate-limited connector gets no button.
 */
function SendNotice() {
  return (
    <div
      role="status"
      className="flex items-start gap-[10px] rounded-row border border-dashed border-dash bg-surface-2 px-[13px] py-[11px]"
    >
      <span className="mt-[1px] flex-none rounded-pill bg-fill-2 px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.6] tracking-[0.04em] text-muted">
        PLANNED · V4
      </span>
      <p className="text-[11.5px] leading-[1.45] text-ink-3 text-pretty">
        Answering arrives with V4 — the one chat engine, where every claim carries the entries,
        transcripts and saves it came from. Your question stays in the field, the scope you set is
        real, and the conversation above is a finished one you can still follow to its sources.
      </p>
    </div>
  )
}
